import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  OnDestroy,
  AfterViewChecked,
  HostListener,
  NgZone,
} from "@angular/core";
import { trigger, transition, style, animate, query } from "@angular/animations";

export const routeFadeAnimation = trigger('routeFadeAnimation', [
  transition('* <=> *', [
    query(':enter', [
      style({ opacity: 0 }),
      animate('180ms ease', style({ opacity: 1 }))
    ], { optional: true })
  ])
]);
import { User, UserService } from "./shared";
import { TranslateService } from "@ngx-translate/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { BrightBlueFormService } from "./shared/services/bright-blue-form.service";
import { SidebarService } from "./shared/services/sidebar.service";
import { DevComponentTaggerService } from "./shared/services/dev-component-tagger.service";
import { TutorialService } from "./shared/services/tutorial.service";
import { UsageTrackerService } from "./shared/services/usage-tracker.service";
import { restoreAdminSession } from "./shared/services/jwt.service";
import { environment } from "../environments/environment";
// import { BUILD_INFO } from "../environments/build-info";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  encapsulation: ViewEncapsulation.None,
  animations: [routeFadeAnimation],
})
export class AppComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  public lang = "";
  public isActive = false;
  public currentUser: User | null = null;
  public hamburgerOpened = false;
  public isCurrentUser = false;
  public isAddUserPage = false;
  public isAddUserPageBackground = false;
  @ViewChild("appNewHeader", { static: false }) appNewHeader: ElementRef;
  private routerSubscription!: Subscription;
  // Sygnatura stanu, od którego zależy wynik setBgImage() — patrz ngAfterViewChecked().
  private lastBgSignature: string | null = null;
  isMobile: boolean = false;
  public brightBlueFormUsing: boolean = false;
  private brightBlueFormUsingSubscription!: Subscription;
  public sidebarCollapsed = false;
  public isDarkMode = false;
  public isLoggingOut = false;
  // Toolbar deweloperski TYLKO gdy build celuje w API dev — decyduje api_url
  // z environmentu (nie flaga production), bo environment.prod.ts bywa przełączany.
  // Na prod (api.sidly-platform.com) badge ma się nigdy nie pokazać.
  public isDevEnvironment = environment.api_url.includes('sidly-platform-dev');
  // Realna data BUILDA (z build-info.ts generowanego przy deployu), nie data
  // załadowania strony — inaczej badge nie mówił nic o świeżości builda.
  // public buildDate = BUILD_INFO.time
  //   ? new Date(BUILD_INFO.time).toLocaleString('pl-PL')
  //   : 'lokalny (ng serve)';
  // public buildHash = BUILD_INFO.hash;
  // public buildBranch = BUILD_INFO.branch;
  public changelogVisible = false;
  public changelogTab: 'deploy' | '24h' | 'todo' = '24h';
  public changelogData: any = null;
  public todosData: any = null;
  public liveBugsData: any = null;
  public liveBugsLoading = false;
  public liveBugsFilter: 'all' | 'unresolved' = 'unresolved';
  public addBugVisible = false;
  public addBugTab = 0;
  public addBugLocation = '';
  public addBugDesc = '';
  public addBugPriority = 'ŚREDNI';
  public addBugSaving = false;
  public statusUpdating: string | null = null;
  private readonly SHEETS_API = 'https://sidly-ai.pl/sheets';

  public languageNames: { [key: string]: string } = {
    en: "English",
    pl: "Polski",
    de: "Deutsch",
    cs: "Čeština",
    fr: "Français",
    pt: "Português",
    it: "Italiano",
    ro: "Română",
    da: "Dansk",
    ar: "العربية",
    bg: "Български",
    no: "Norsk",
    nl: "Nederlands",
    sr: "Српski",
    et: "Eesti",
    lv: "Latviešu",
    lt: "Lietuvių",
    sl: "Slovenščina",
    sk: "Slovenčina",
    es: "Español",
  };

  public addUserPages = [
    "/indiv-client/add-user/",
    "/indiv-client/accept-documents/",
    "/indiv-client/edit-user/",
    "/users/add-user-health/",
    "/users/configuration-user",
    "/users/edit-user",
    "/users/sos-request",
    "/projects/addProject",
    "/settings/configuration",
    "/settings/mfa",
    "/settings/forwarding",
    "/caregivers/add-caregiver",
    "/users/add-user",
    "/manager/map-localization",
    "/caregivers/map-localization",
    "/caregivers/edit-caregiver/",
    "/caregivers/edit-caregiver-c",
    "/users/add-user",
    "/users/sos-request",
    "/settings/add-beacon",
    "/projects/assignToProject/",
  ];

  constructor(
    private userService: UserService,
    private el: ElementRef,
    public translate: TranslateService,
    public router: Router,
    private brightBlueFormService: BrightBlueFormService,
    private sidebarService: SidebarService,
    private devComponentTagger: DevComponentTaggerService,
    private tutorialService: TutorialService,
    private zone: NgZone,
    private usageTracker: UsageTrackerService,
  ) {
    this.setupLanguage();
    this.setupRouterEvents();
    this.router.events.subscribe(() => {
      this.isActive =
        this.router.url.startsWith("/auth/register") ||
        this.router.url.startsWith("/start?chosenCountry=");
    });

    this.sidebarService.toggleSidebar$.subscribe(() => this.toggleSidebar());
    this.sidebarService.collapsed$.subscribe(isCollapsed => {
      this.sidebarCollapsed = isCollapsed;
    });
  }

  private setupLanguage() {
    const supportedLangs = [
      "en",
      "pl",
      "de",
      "cs",
      "fr",
      "pt",
      "it",
      "ro",
      "da",
      "ar",
      "bg",
      "no",
      "nl",
      "sr",
      "et",
      "lv",
      "lt",
      "sl",
      "sk",
      "es",
    ];

    this.translate.addLangs(supportedLangs);

    // Angielski idzie obok translate.use, bo jego ładowanie przestawia translate.get na plik, który właśnie się wczytuje.
    this.translate.onLangChange.pipe(take(1)).subscribe(() =>
      this.translate.currentLoader.getTranslation("en").subscribe((english) => {
        this.translate.setTranslation("en", english);
        this.translate.setDefaultLang("en");
      })
    );

    const savedLang = window.localStorage["lang"];
    if (savedLang) {
      this.translate.use(savedLang);
    } else {
      const browserLang = this.translate.getBrowserLang();
      const validLang = supportedLangs.includes(browserLang)
        ? browserLang
        : "en";
      this.translate.use(validLang);
      window.localStorage["lang"] = validLang;
    }

    this.lang = window.localStorage["lang"];
  }

  private setupRouterEvents() {
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setBgImage();
        this.checkAddUserPage();
        // KLUCZOWA ZMIANA: Resetuj brightBlueForm jeśli nie jesteś na stronie z formą
        this.checkAndResetBrightBlueForm();
      }
    });
  }

  // NOWA METODA: Sprawdza czy aktualny URL to strona z bright blue formą
  private checkAndResetBrightBlueForm() {
    const currentUrl = this.router.url;
    const isBrightBlueFormPage = this.addUserPages.some((path) =>
      currentUrl.startsWith(path),
    );

    // Jeśli nie jesteś na stronie z formą i brightBlueForm jest aktywny - zresetuj go
    if (!isBrightBlueFormPage && this.brightBlueFormUsing) {
      this.brightBlueFormService.setBrightBlueFormStatus(false);
    }
  }

  toggleSidebar() {
    this.sidebarService.setCollapsed(!this.sidebarCollapsed);
  }

  @HostListener("window:resize", ["$event"])
  onResize(event: Event) {
    this.checkIfMobile();
  }

  checkIfMobile() {
    const width = window.innerWidth;
    this.isMobile = width <= 1024;
  }

  public changeLanguage(lang: string): void {
    window.localStorage["lang"] = lang;
    this.lang = lang;
    location.reload();
  }

  // ===== Tryb „Zaloguj jako manager" (impersonacja przez admina) =====
  get impersonating(): boolean {
    return window.localStorage.getItem('impersonateActive') === '1';
  }
  get impersonateEmail(): string {
    return window.localStorage.getItem('impersonateManagerEmail') || '';
  }
  exitImpersonation(): void {
    restoreAdminSession();
    window.location.href = '/admin/managers';   // pełny reload — powrót jako admin
  }

  ngOnInit() {
    this.usageTracker.start();
    this.devComponentTagger.init();
    this.checkIfMobile();
    this.userService.populate();
    const interval = setInterval(() => {
      const currentUser = this.userService.getCurrentUser();
      if (currentUser && currentUser.hasOwnProperty("user_id")) {
        this.currentUser = currentUser;
        this.isCurrentUser = true;
        clearInterval(interval);
        this.tutorialService.setUserContext({
          role: currentUser.role || currentUser.user_id.role,
          type: currentUser.user_id.type,
          privilege: currentUser.user_id.privilege || '',
          isIndividual: currentUser.user_id.if_indiv_user || false
        });
        this.tutorialService.checkFirstLogin();
      }
    }, 100);

    this.setBgImage();

    const savedTheme = localStorage.getItem("theme");
    this.isDarkMode = savedTheme === "dark";
    this.applyTheme();

    this.userService.isLoggingOut$.subscribe((value) => {
      this.isLoggingOut = value;
    });

    this.brightBlueFormUsingSubscription =
      this.brightBlueFormService.brightBlueFormUsing$.subscribe((value) => {
        this.brightBlueFormUsing = value;
        this.updateIndivConfClass();
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setBgImage();
    }, 100);
  }

  // Ten hook MUSI zostać: klasa `.bg-img` nadawana jest przez ngClass w szablonie, a tło
  // ustawiamy imperatywnie przez querySelector. Wywołanie z NavigationEnd trafia jeszcze
  // w DOM sprzed renderu (klasy nie ma → querySelector zwraca null), więc bez wywołania
  // po sprawdzeniu widoku tło nie pojawia się po przejściu z ekranu logowania.
  //
  // Wcześniej jednak KAŻDY cykl detekcji zmian planował setTimeout + querySelector + zapis
  // stylu — a cykl odpala się m.in. przy każdym zdarzeniu scrolla (HostListener na
  // Powiadomieniach), każdej odpowiedzi HTTP i każdym evencie socketu. Realnie wynik
  // setBgImage() zależy tylko od adresu (isLoginPage/isHelpPage), isCurrentUser
  // i brightBlueFormUsing, więc porównujemy te trzy rzeczy i pracujemy dopiero przy zmianie.
  ngAfterViewChecked() {
    const signature = `${this.router.url}|${this.isCurrentUser}|${this.brightBlueFormUsing}`;
    if (signature === this.lastBgSignature) {
      return;
    }
    this.lastBgSignature = signature;

    setTimeout(() => {
      this.setBgImage();
    }, 100);
  }

  onDeactivate() {
    window.scrollTo(0, 0);
  }

  public setBgImage() {
    const bgElement = this.el.nativeElement.querySelector(".bg-img");
    if (bgElement) {
      if (!this.isLoginPage() && !this.isHelpPage() && !this.isCurrentUser) {
        bgElement.style.backgroundImage = 'url("./assets/images/bg.jpg")';
        bgElement.classList.add("bg-photo");
      } else {
        bgElement.style.backgroundImage = "";
        bgElement.classList.remove("bg-photo");
      }
    }

    this.updateIndivConfClass();
  }

  private updateIndivConfClass() {
    const bodyElement = document.body;
    const currentUrl = this.router.url;

    if (
      this.brightBlueFormUsing &&
      !currentUrl.startsWith("/users/sos-request")
    ) {
      bodyElement.classList.add("indiv-conf");
    } else {
      bodyElement.classList.remove("indiv-conf");
    }
  }

  toggleMenu() {
    if (this.appNewHeader) {
      this.appNewHeader.nativeElement.classList.toggle("expanded");
    }
    window.scrollTo(0, 0);
    this.hamburgerOpened = !this.hamburgerOpened;
  }

  isLoginPage(): boolean {
    return this.router.url == "/auth/login";
  }

  isHelpPage(): boolean {
    return this.router.url == "/help";
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.brightBlueFormUsingSubscription) {
      this.brightBlueFormUsingSubscription.unsubscribe();
    }
  }

  checkAddUserPage() {
    const currentUrl = this.router.url;
    this.isAddUserPage = this.addUserPages.some((path) =>
      currentUrl.startsWith(path),
    );

    this.isAddUserPageBackground = this.addUserPages.some((path) =>
      currentUrl.startsWith(path),
    );

    if (this.isAddUserPage) {
      document.body.classList.add('fullscreen-page');
      if (this.sidebarCollapsed) {
        this.toggleSidebar();
      }
    } else {
      document.body.classList.remove('fullscreen-page');
    }
  }

  public toggleChangelog() {
    this.changelogVisible = !this.changelogVisible;
    if (this.changelogVisible && !this.changelogData) {
      fetch('/assets/changelog.json')
        .then(r => r.json())
        .then(data => this.zone.run(() => this.changelogData = data))
        .catch(() => this.zone.run(() => this.changelogData = { commitsSinceDeploy: [], commitsLast24h: [] }));
    }
  }

  public switchToTodo() {
    this.changelogTab = 'todo';
    this.loadLiveBugs();
  }

  private loadLiveBugs() {
    if (this.liveBugsLoading) return;
    this.liveBugsLoading = true;
    fetch(this.SHEETS_API + '/bugs')
      .then(r => r.json())
      .then(data => {
        this.zone.run(() => {
          this.liveBugsData = data;
          this.liveBugsLoading = false;
        });
      })
      .catch(() => {
        this.zone.run(() => {
          this.liveBugsLoading = false;
          // Fallback to static todos.json
          if (!this.todosData) {
            fetch('/assets/todos.json')
              .then(r => r.json())
              .then(d => this.zone.run(() => this.todosData = d))
              .catch(() => this.zone.run(() => this.todosData = { tabs: [], totalUnresolved: 0 }));
          }
        });
      });
  }

  public refreshBugs() {
    this.liveBugsData = null;
    this.loadLiveBugs();
  }

  public updateBugStatus(tabIndex: number, row: number, field: string, value: string, item: any) {
    const key = `${tabIndex}-${row}-${field}`;
    this.statusUpdating = key;
    fetch(this.SHEETS_API + '/bugs/status', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tab_index: tabIndex, row, field, value })
    })
      .then(r => r.json())
      .then(res => {
        this.zone.run(() => {
          if (res.status === 'success') {
            item[field] = value;
          }
          this.statusUpdating = null;
        });
      })
      .catch(() => this.zone.run(() => this.statusUpdating = null));
  }

  public openAddBug() {
    this.addBugVisible = !this.addBugVisible;
    if (this.addBugVisible) {
      this.addBugLocation = this.router.url;
    }
  }

  public addBug() {
    if (!this.addBugDesc.trim()) return;
    this.addBugSaving = true;
    fetch(this.SHEETS_API + '/bugs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tab_index: this.addBugTab,
        location: this.addBugLocation,
        description: this.addBugDesc,
        priority: this.addBugPriority
      })
    })
      .then(r => r.json())
      .then(res => {
        this.zone.run(() => {
          this.addBugSaving = false;
          if (res.status === 'success') {
            this.addBugLocation = '';
            this.addBugDesc = '';
            this.addBugPriority = 'ŚREDNI';
            this.addBugVisible = false;
            this.refreshBugs();
          }
        });
      })
      .catch(() => this.zone.run(() => this.addBugSaving = false));
  }

  public getFilteredItems(items: any[]): any[] {
    if (this.liveBugsFilter === 'all') return items;
    return items.filter(i => {
      const t = (i.tester_result || '').toUpperCase();
      return t !== 'POZYTYWNY' && t !== 'ODRZUCONO';
    });
  }

  public getStatusClass(item: any): string {
    const t = (item.tester_result || '').toUpperCase();
    if (t === 'POZYTYWNY') return 'status-ok';
    if (t === 'NEGATYWNY') return 'status-fail';
    if (t === 'ODRZUCONO') return 'status-rejected';
    if (t === 'DO TESTU' || t === 'DO SPRAWDZENIA') return 'status-testing';
    if (t === 'IN PROGRESS') return 'status-progress';
    if (t === 'WAITING') return 'status-waiting';
    return '';
  }

  public toggleTheme() {
    this.isDarkMode = !this.isDarkMode;
    this.applyTheme();
    localStorage.setItem("theme", this.isDarkMode ? "dark" : "light");
  }

  private applyTheme() {
    if (this.isDarkMode) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
  }
}
